// The selection table's pure logic — how a roster row reads, how it sorts, and
// which rows are on screen as the reader scrolls.
//
// Named for what a row IS rather than for the screen, so it cannot collide
// with StudentSelectionTable.js on a case-insensitive filesystem.

import { EM_DASH } from "./studentReportTable";

export const ROSTER_SORT_KEYS = {
  NAME: "student_name",
  SESSION_COUNT: "session_count",
  TOTAL_MINUTES: "total_minutes",
  LAST_SESSION: "last_session",
};

/** "Student Name (153)" — the roster's size, on the column that names it. */
export const studentNameHeader = (total) => `Student Name (${total})`;

/**
 * Minutes as this screen spells them: "66 Min".
 *
 * The Session Detail tab spells the same number "66 MIN". Both are their own
 * frame's wording; unifying them would make one of the two screens wrong.
 */
export const rosterMinutesText = (value) =>
  value === null || value === undefined ? EM_DASH : `${Math.round(value)} Min`;

/**
 * A date as this screen spells it: "8/31/26", month unpadded.
 *
 * Split rather than parsed. `new Date("2026-08-31")` is UTC midnight, which is
 * 30 August in every US time zone — and the server sends a bare date, so there
 * is no offset to save it.
 */
export const rosterDateText = (value) => {
  if (!value) return EM_DASH;
  const [year, month, day] = String(value).slice(0, 10).split("-");
  // Parsed, not merely present: `Number("x")` is NaN, and a malformed value
  // would render "8/NaN/26" rather than falling back to the em dash.
  const parts = [Number(month), Number(day)];
  if (!year || !parts.every(Number.isFinite)) return EM_DASH;
  return `${parts[0]}/${parts[1]}/${year.slice(2)}`;
};

/** A student with no Firebase name still has a row, and it still says who. */
export const rosterNameText = (row) => row?.display_name || "Unknown Student";

const sortValue = (row, column) => {
  switch (column) {
    case ROSTER_SORT_KEYS.NAME:
      return rosterNameText(row).toLowerCase();
    case ROSTER_SORT_KEYS.SESSION_COUNT:
      return row.session_count ?? null;
    case ROSTER_SORT_KEYS.TOTAL_MINUTES:
      return row.total_minutes ?? null;
    case ROSTER_SORT_KEYS.LAST_SESSION:
      return row.last_session_date || null;
    default:
      return null;
  }
};

/** Ascending, descending, then back to the order the server sent. */
export const nextRosterSort = (current, column) => {
  if (!current || current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
};

/**
 * Sort a COPY of the whole roster, not the page on screen.
 *
 * Sorting the visible ten would order within page 3 rather than finding the
 * busiest student, which is the only reason the column sorts at all.
 *
 * A missing value sorts LAST IN BOTH DIRECTIONS: a student we failed to time
 * is not a student with the least time.
 */
export const sortRosterRows = (rows = [], sort = null) => {
  if (!sort?.column) return rows;
  const factor = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((left, right) => {
    const a = sortValue(left, sort.column);
    const b = sortValue(right, sort.column);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (a === b) return 0;
    return (a < b ? -1 : 1) * factor;
  });
};

/**
 * How many rows are on screen before the reader scrolls, and how many more
 * arrive each time they reach the end.
 *
 * The frame drew ten rows and a pager; the pager is gone -- the ticket asks
 * for endless scrolling instead -- so ten was never a page size, it was what
 * fitted above the buttons. These are the numbers the only other endless table
 * in the product uses (School Reports), and at fifty most teachers never reach
 * the end at all: the largest non-anonymous roster in production today is
 * smaller than one batch.
 */
export const INITIAL_VISIBLE = 50;
export const VISIBLE_CHUNK = 50;

/**
 * The rows drawn so far.
 *
 * Sorting orders the WHOLE roster before this cuts it, so sorting by session
 * count finds the busiest student rather than the busiest of the fifty already
 * on screen.
 */
export const visibleRows = (rows = [], count = INITIAL_VISIBLE) =>
  rows.slice(0, count);
